<template>
  <div ref="theProductListFilters" :class="[bemBlock, 'header-popup']">
    <div :class="[bemElement('header')]">
      <span
        :class="[bemElement('filters-count-text'), bemElement('header-text')]"
      >
        {{ $msg('filters.quantity.text', 'filters', [uiNumSelectedFilters]) }}
      </span>
      <button
        :class="[
          bemElement('filters-clear-all'),
          bemElement('header-text'),
          'u-btn-clean',
        ]"
        @click="productSearch && handleClearAllClick()"
      >
        {{ $msg('filters.clearall.text', 'filters') }}
      </button>
    </div>
    <button
      :class="[
        bemElement('btn-close'),
        'header-popup__btn-close',
        'btn-close',
        'u-btn-clean',
      ]"
      @click="sendAnalyticsEventClosed"
    >
      <span class="visually-hidden">
        {{ $msg('global.close', 'locale') }}
      </span>
    </button>
    <BaseFiltersSorting
      :loading="loading"
      :options="sortingOptions"
      :selected="selectedSortingOption"
      @select="selectSortingOption"
    />
    <BaseFiltersSize
      :loading="loading"
      :refinement="sizeRefinement"
      :selected="selectedFilters"
      @select="selectFilter"
      @deselect="deselectFilter"
    />
    <BaseFiltersColor
      :loading="loading"
      :refinement="colorRefinement"
      :selected="selectedFilters"
      @select="selectFilter"
      @deselect="deselectFilter"
    />
    <BaseFiltersPrice
      v-if="!isGlobaleEnabled"
      :loading="loading"
      :refinement="priceRefinement"
      :selected="selectedFilters"
      @select="selectFilter"
      @deselect="deselectPriceFilter"
    />
    <button
      v-if="!applyFiltersDirectly"
      :class="[
        bemElement('btn-apply'),
        'btn',
        {
          'btn--primary': isBreakpointDesktop,
          'btn--cta': isBreakpointMobile,
        },
      ]"
      @click="productSearch && handleApplyClick()"
    >
      {{ $msg('filters.button.apply', 'filters') }}
    </button>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { getters, types } from '../store';
import bemClassesMixin from '../mixins/bemClassesMixin';
import windowResizeMixin from '../mixins/windowResizeMixin';
import getAnalyticsEventBus from '../../common/getAnalyticsEventBus';
import getFrontConfig from '../../common/getFrontConfig';
import { getUrlForFilters } from '../utils/productSearch';
import BaseFiltersColor from './BaseFiltersColor.vue';
import BaseFiltersPrice from './BaseFiltersPrice.vue';
import BaseFiltersSize from './BaseFiltersSize.vue';
import BaseFiltersSorting from './BaseFiltersSorting.vue';
import EventBusConfig from '../config/eventBusConfig';

const analyticsEventBus = getAnalyticsEventBus();
const {
  isGlobaleEnabled,
  responsive: {
    // eslint-disable-next-line camelcase
    bp_575,
    // eslint-disable-next-line camelcase
    bp_768,
  },
} = getFrontConfig();

export default {
  name: 'TheProductListFilters',

  components: {
    BaseFiltersColor,
    BaseFiltersPrice,
    BaseFiltersSize,
    BaseFiltersSorting,
  },

  mixins: [bemClassesMixin, windowResizeMixin],

  props: {},

  data() {
    return {
      dataChangeBreakpoint: bp_575,
      isGlobaleEnabled,
    };
  },

  computed: {
    ...mapState({
      loading: (state) => state.productList.loading,
      productSearch: (state) => state.productList.productSearch,
      refinements: (state) => state.productList.ui.initialRefinements,
      selectedFilters: (state) => state.productList.ui.selectedFilters,
      sortingOptions: (state) => state.productList.ui.initialSortingOptions,
      selectedSortingOption: (state) =>
        state.productList.ui.selectedSortingOption,
    }),
    ...mapGetters({
      uiNumSelectedFilters: getters.productList.uiNumSelectedFilters,
    }),
    applyFiltersDirectly() {
      // eslint-disable-next-line camelcase
      return this.$windowWidth >= bp_768;
    },
    colorRefinement() {
      return this.refinements?.find(
        (refinement) =>
          refinement.isAttributeRefinement &&
          refinement.values[0]?.id === 'bylUnifiedColor'
      );
    },
    /**
     * Returns price refinement if it is enabled
     * @returns {ProductSearchRefinement} Price refinement
     */
    priceRefinement() {
      return this.refinements?.find(
        (refinement) => refinement.isPriceRefinement
      );
    },
    sizeRefinement() {
      return this.refinements?.find(
        (refinement) =>
          refinement.isAttributeRefinement &&
          refinement.values[0]?.id === 'size'
      );
    },
  },

  mounted() {
    const { theProductListFilters } = this.$refs;
    this.$eventBus.$on(
      EventBusConfig.commands.TheProductList.openFilters,
      async () => {
        await $('body').trigger(
          'header-popup:show',
          `.${theProductListFilters.classList[0]}`
        );
        this.sendAnalyticsEventOpened();
        theProductListFilters.scrollTo(0, 0);
      }
    );
    this.$eventBus.$on(
      EventBusConfig.commands.TheProductList.clearFilters,
      this.handleClearAllClick
    );
  },

  beforeDestroy() {
    this.$eventBus.$off(EventBusConfig.commands.TheProductList.openFilters);
    this.$eventBus.$off(EventBusConfig.commands.TheProductList.clearFilters);
  },

  methods: {
    handleFilterSelection() {
      if (this.productSearch && this.applyFiltersDirectly) {
        this.handleApplyClick();
      }
    },
    handleApplyClick() {
      const urlWithSelectedFilters = getUrlForFilters(
        this.productSearch,
        this.selectedFilters,
        this.selectedSortingOption
      );
      this.$store.commit(types.productList.applyFilters);
      this.$store
        .dispatch(
          types.productList.loadFilteredListFromUrl,
          urlWithSelectedFilters
        )
        .then(this.sendAnalyticsEventApplied);
      if (!this.applyFiltersDirectly) {
        $('body').trigger('header-popup:hide');
      }
      window.scrollTo(0, 0);
      this.$emit('filter-applied');
    },
    handleClearAllClick() {
      const urlWithNoFilters = getUrlForFilters(this.productSearch);
      this.$store.commit(types.productList.resetUiFilters);
      this.$store
        .dispatch(types.productList.loadFilteredListFromUrl, urlWithNoFilters)
        .then(this.sendAnalyticsEventReset);
      window.scrollTo(0, 0);
      this.$emit('filter-cleared');
    },
    selectFilter(refinementValue) {
      this.$store.commit(types.productList.selectFilter, refinementValue);
      this.handleFilterSelection();
    },
    deselectFilter(refinementValue) {
      this.$store.commit(types.productList.deselectFilter, refinementValue);
      this.handleFilterSelection();
    },
    selectSortingOption(option) {
      this.$store.commit(types.productList.selectSortingOption, option);
      this.handleFilterSelection();
    },
    /**
     * Remove price filtering from store
     * @returns {undefined}
     */
    deselectPriceFilter() {
      this.$store.commit(types.productList.deselectPriceFilter);
      this.handleFilterSelection();
    },
    /**
     * Emit the 'applied' event
     */
    sendAnalyticsEventApplied() {
      analyticsEventBus.publish(
        analyticsEventBus.EVENTS.productListFiltersApplied,
        {
          selectedFilters: this.selectedFilters,
          selectedSortingOption: this.selectedSortingOption,
        }
      );
    },
    /**
     * Emit the 'closed' event
     */
    sendAnalyticsEventClosed() {
      analyticsEventBus.publish(
        analyticsEventBus.EVENTS.productListFiltersClosed
      );
    },
    /**
     * Emit the 'opened' event
     */
    sendAnalyticsEventOpened() {
      analyticsEventBus.publish(
        analyticsEventBus.EVENTS.productListFiltersOpened
      );
    },
    /**
     * Emit the 'reset' event
     */
    sendAnalyticsEventReset() {
      analyticsEventBus.publish(
        analyticsEventBus.EVENTS.productListFiltersReset
      );
    },
  },
};
</script>
